import React, { useEffect } from 'react';
import styled from 'styled-components';
import AOS from 'aos';
import IconButton from '@mui/material/IconButton';
import { MdArrowForward } from '@react-icons/all-files/md/MdArrowForward';
import { Link } from 'gatsby';
import {
  Text,
  Box,
  Flex,
  Wrapper,
  GatsbyImage,
  ReferralTable,
} from '../components';
import { theme } from '../style';
import 'aos/dist/aos.css';

const ImageBox = styled(Flex)`
  @media ${theme.devices.sm} {
    order: 1;
    margin-bottom: 42px;
  }
`;

const FeaturesBox = styled(Box)`
  @media ${theme.devices.sm} {
    order: 2;
  }
`;

const Description = styled(Text)`
  white-space: pre-line;
`;

const List = styled.div`
  ul {
    color: ${theme.colors.grey.v5};
    margin-top: 32px;
    padding-left: ${({ listStyle }) => (listStyle ? '22px' : '16px')};
    list-style-image: ${({ listStyle }) => `url(${listStyle})`};

    li {
      padding-left: ${({ listStyle }) => (listStyle ? '12px' : '0')};
      margin: ${({ listStyle }) => (listStyle ? '16px 0' : '0')};
    }

    p {
      margin-top: 0;
      margin-bottom: 0;

      :first-child {
        margin-top: 16px;
      }
    }
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 0 !important;
  width: 32px;
  height: 32px;
  background-color: #4a90e2 !important;
  transition: all 0.2s !important;

  :hover {
    background-color: #1f68c2 !important;
    color: white;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${theme.colors.grey.v5};
  padding-right: 20px;
  transition: all 0.2s;

  :hover {
    padding-right: 24px;
  }
`;

const getImageBox = (image) => (
  <ImageBox
    width={[1, 1, 5 / 12, 1 / 2]}
    justifyContent="center"
    data-aos={image.position === 'Left' ? 'fade-right' : 'fade-left'}
    px={3}
  >
    {image.src && (
      <GatsbyImage
        src={image.src}
        alt={image.alt}
        maxwidth={image.maxWidth || 'none'}
        maxheight={image.maxHeight || 'none'}
        borderradius={image.imageStyle === 'Rounded' ? '20px' : ''}
        hover={image.imageStyle === 'Rounded' ? 1 : 0}
      />
    )}
  </ImageBox>
);

const FeaturesBlock = ({
  data,
  listStyle,
  hasBackground,
  px = 5,
  py = 5,
  pl,
  pr,
}) => {
  const { image, details } = data || {};

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <Box backgroundColor={hasBackground ? theme.colors.grey.v1 : 'white'}>
      <Wrapper>
        <Flex
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          px={[0, 0, 2, px]}
          py={[py]}
          pl={pl && [0, 0, 2, pl]}
          pr={pr && [0, 0, 2, pr]}
        >
          {image.position === 'Left' && getImageBox(image)}

          <FeaturesBox width={[1, 1, 7 / 12, 1 / 2]} px={3}>
            {details?.title && (
              <Text as="h5" fontWeight="bold" mb={4}>
                {details.title}
              </Text>
            )}
            {details?.description && (
              <Description
                color="grey.v5"
                dangerouslySetInnerHTML={{ __html: details.description }}
              />
            )}
            {details?.features && (
              <List
                listStyle={listStyle}
                dangerouslySetInnerHTML={{ __html: details.features }}
              />
            )}
            {details?.table?.body && Array.isArray(details.table.body) && (
              <ReferralTable data={details.table} />
            )}
            {details?.link && (
              <Flex alignItems="center" width="auto">
                <StyledLink to={details.link.to}>
                  {details.link.name}
                </StyledLink>
                <StyledIconButton component={Link} to={details.link.to}>
                  <MdArrowForward size={24} />
                </StyledIconButton>
              </Flex>
            )}
          </FeaturesBox>

          {image.position === 'Right' && getImageBox(image)}
        </Flex>
      </Wrapper>
    </Box>
  );
};

export default FeaturesBlock;
